const API = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export interface OrderItem {
  productId: string;
  name: string;        // mapped from productName on backend
  price: number;
  imageUrl: string;    // mapped from productImageUrl on backend
  quantity: number;
  selectedSize?: string;
  selectedColor?: string;
  subtotal?: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  status: string;
  shippingAddress: string;
  billingAddress: string;
  paymentMethod: string;
  paymentStatus: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export const orderService = {
  async getOrders(): Promise<Order[]> {
    const res = await fetch(`${API}/api/orders`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch orders');
    const raw: any[] = await res.json();
    return raw.map(mapOrder);
  },

  async getOrderById(orderId: string): Promise<Order | null> {
    const res = await fetch(`${API}/api/orders/${orderId}`, { headers: authHeaders() });
    if (!res.ok) return null;
    return mapOrder(await res.json());
  },

  async cancelOrder(orderId: string): Promise<Order> {
    const res = await fetch(`${API}/api/orders/${orderId}/status`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ status: 'cancelled' }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Cannot cancel order');
    }
    return mapOrder(await res.json());
  },
};

// Map backend response (which has nested product, productName, etc.) to flat OrderItem
function mapOrder(o: any): Order {
  return {
    id: o.id ?? '',
    orderNumber: o.orderNumber ?? '',
    totalAmount: Number(o.totalAmount) || 0,
    status: o.status ?? 'pending',
    shippingAddress: o.shippingAddress ?? '',
    billingAddress: o.billingAddress ?? '',
    paymentMethod: o.paymentMethod ?? '',
    paymentStatus: o.paymentStatus ?? '',
    notes: o.notes,
    createdAt: o.createdAt ?? new Date().toISOString(),
    updatedAt: o.updatedAt,
    items: (o.items ?? []).map((item: any) => ({
      productId: item.productRefId ?? item.productId ?? item.product?.id ?? '',
      name: item.productName ?? item.product?.name ?? 'Product',
      price: Number(item.price) || 0,
      imageUrl: item.productImageUrl ?? item.product?.imageUrl ?? '',
      quantity: Number(item.quantity) || 1,
      selectedSize: item.selectedSize ?? undefined,
      selectedColor: item.selectedColor ?? undefined,
      subtotal: Number(item.subtotal) || 0,
    })),
  };
}
